import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'flag-icons/css/flag-icons.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS } from '../config/api';

function RegistrationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan || {
    id: 'essential',
    name: 'Essential',
    price: 99
  };

  const [formData, setFormData] = useState({
    // Company Details
    companyName: '',
    // Structured address fields (auto-filled by Google Places)
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    taxRegistrationNumber: '',
    companyLogo: null,

    // Super User Details
    fullName: '',
    designation: '',
    email: '',
    phoneCountryCode: '+1',
    phoneCountryIso: 'us',
    phoneNumber: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    password: '',
    confirmPassword: '',
    
    // Agreement
    termsAgreed: false
  });

  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const addressRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Country codes data ──────────────────────────────────────────────────
  const COUNTRY_CODES = [
    { code: '+1',   country: 'United States',    iso: 'us' },
    { code: '+1', country: 'Canada', iso: 'ca' },
    { code: '+44', country: 'United Kingdom', iso: 'gb' },
    { code: '+91', country: 'India', iso: 'in' },
    { code: '+61', country: 'Australia', iso: 'au' },
    { code: '+49', country: 'Germany', iso: 'de' },
    { code: '+33', country: 'France', iso: 'fr' },
    { code: '+81', country: 'Japan', iso: 'jp' },
    { code: '+86', country: 'China', iso: 'cn' },
    { code: '+55', country: 'Brazil', iso: 'br' },
    { code: '+52', country: 'Mexico', iso: 'mx' },
    { code: '+27', country: 'South Africa', iso: 'za' },
    { code: '+971', country: 'UAE', iso: 'ae' },
    { code: '+966', country: 'Saudi Arabia', iso: 'sa' },
    { code: '+65', country: 'Singapore', iso: 'sg' },
    { code: '+60', country: 'Malaysia', iso: 'my' },
    { code: '+63', country: 'Philippines', iso: 'ph' },
    { code: '+62', country: 'Indonesia', iso: 'id' },
    { code: '+82', country: 'South Korea', iso: 'kr' },
    { code: '+39', country: 'Italy', iso: 'it' },
    { code: '+34', country: 'Spain', iso: 'es' },
    { code: '+31', country: 'Netherlands', iso: 'nl' },
    { code: '+46', country: 'Sweden', iso: 'se' },
    { code: '+47', country: 'Norway', iso: 'no' },
    { code: '+45', country: 'Denmark', iso: 'dk' },
    { code: '+358', country: 'Finland', iso: 'fi' },
    { code: '+41', country: 'Switzerland', iso: 'ch' },
    { code: '+43', country: 'Austria', iso: 'at' },
    { code: '+32', country: 'Belgium', iso: 'be' },
    { code: '+351', country: 'Portugal', iso: 'pt' },
    { code: '+30', country: 'Greece', iso: 'gr' },
    { code: '+48', country: 'Poland', iso: 'pl' },
    { code: '+420', country: 'Czech Republic', iso: 'cz' },
    { code: '+380', country: 'Ukraine', iso: 'ua' },
    { code: '+7', country: 'Russia', iso: 'ru' },
    { code: '+90', country: 'Turkey', iso: 'tr' },
    { code: '+20', country: 'Egypt', iso: 'eg' },
    { code: '+234', country: 'Nigeria', iso: 'ng' },
    { code: '+254', country: 'Kenya', iso: 'ke' },
    { code: '+94', country: 'Sri Lanka', iso: 'lk' },
    { code: '+92', country: 'Pakistan', iso: 'pk' },
    { code: '+880', country: 'Bangladesh', iso: 'bd' },
    { code: '+64', country: 'New Zealand', iso: 'nz' },
    { code: '+353', country: 'Ireland', iso: 'ie' },
    { code: '+972', country: 'Israel', iso: 'il' },
    { code: '+66', country: 'Thailand', iso: 'th' },
    { code: '+84', country: 'Vietnam', iso: 'vn' },
    { code: '+54', country: 'Argentina', iso: 'ar' },
    { code: '+56', country: 'Chile', iso: 'cl' },
    { code: '+57', country: 'Colombia', iso: 'co' },
    { code: '+51', country: 'Peru', iso: 'pe' },
  ];

  const filteredCountries = countrySearch
    ? COUNTRY_CODES.filter(c =>
        c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.code.includes(countrySearch) || c.iso.includes(countrySearch.toLowerCase())
      )
    : COUNTRY_CODES;

  // ── Close dropdowns on outside click ────────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (addressRef.current && !addressRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
        setCountrySearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Google Places autocomplete — new 2025 API ───────────────────────────
  const sessionTokenRef = useRef(null);

  const getOrCreateSessionToken = useCallback(() => {
    if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3) { setAddressSuggestions([]); return; }
    if (!window.google?.maps?.places?.AutocompleteSuggestion) {
      setAddressSuggestions([]); return;
    }
    setAddressLoading(true);
    try {
      const token = getOrCreateSessionToken();
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion
        .fetchAutocompleteSuggestions({ input, sessionToken: token });
      const mapped = (suggestions || []).map(s => ({
        place_id: s.placePrediction?.placeId || '',
        description: s.placePrediction?.text?.text || '',
        structured_formatting: {
          main_text:      s.placePrediction?.mainText?.text || '',
          secondary_text: s.placePrediction?.secondaryText?.text || '',
        },
        _placePrediction: s.placePrediction,
      }));
      setAddressSuggestions(mapped);
    } catch { setAddressSuggestions([]); }
    finally { setAddressLoading(false); }
  }, [getOrCreateSessionToken]);

  const handleAddressSearch = (e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, addressLine1: val }));
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
    if (errors.addressLine1) setErrors(prev => ({ ...prev, addressLine1: '' }));
  };

  const handleSelectSuggestion = async (placeId, description, _placePrediction) => {
    setShowSuggestions(false);
    setAddressSuggestions([]);
    setFormData(prev => ({ ...prev, addressLine1: description }));
    sessionTokenRef.current = null;
    try {
      let place;
      if (_placePrediction?.toPlace) {
        place = _placePrediction.toPlace();
      } else if (window.google?.maps?.places?.Place) {
        place = new window.google.maps.places.Place({ id: placeId });
      } else { return; }
      await place.fetchFields({ fields: ['addressComponents'] });
      const comps = place.addressComponents || [];
      const get = (type) => comps.find(c => c.types?.includes(type))?.longText || '';
      const streetNumber = get('street_number');
      const streetName   = get('route');
      setFormData(prev => ({
        ...prev,
        addressLine1:  (streetNumber ? streetNumber + ' ' + streetName : streetName).trim() || description,
        city:          get('locality') || get('postal_town') || get('sublocality_level_1') || '',
        stateProvince: get('administrative_area_level_1') || '',
        postalCode:    get('postal_code') || '',
        country:       get('country') || '',
      }));
    } catch(e) { console.warn('[Places]', e); }
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({ ...prev, companyLogo: 'Logo must be less than 5MB' }));
        return;
      }
      
      setFormData(prev => ({ ...prev, companyLogo: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Company validation
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address line 1 is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal / ZIP code is required';
    if (!formData.taxRegistrationNumber.trim()) newErrors.taxRegistrationNumber = 'Tax registration number is required';

    // User validation
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else {
      // Block personal/free email domains - must use official company email
      const blockedDomains = [
        'gmail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk', 'yahoo.in',
        'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
        'aol.com', 'icloud.com', 'me.com', 'mac.com',
        'mail.com', 'email.com', 'usa.com', 'ymail.com',
        'protonmail.com', 'proton.me', 'zoho.com', 'zohomail.com',
        'rediffmail.com', 'rediff.com', 'inbox.com',
        'gmx.com', 'gmx.net', 'yandex.com', 'yandex.ru',
        'mailinator.com', 'guerrillamail.com', 'tempmail.com',
      ];
      const emailDomain = formData.email.split('@')[1]?.toLowerCase();
      if (blockedDomains.includes(emailDomain)) {
        newErrors.email = 'Please use your official company email address. Personal email addresses (Gmail, Yahoo, Hotmail, etc.) are not allowed.';
      }
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[0-9\s\-().]{6,15}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Enter a valid phone number (digits only, 6–15 characters)';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.termsAgreed) {
      newErrors.termsAgreed = 'You must read and agree to the Subscription Agreement before registering';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState('terms'); // 'terms' | 'privacy'

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // ── STEP 1: Create account ──────────────────────────────────────────
      setSubmitStep('Creating your account…');

      const formDataToSend = new FormData();
      formDataToSend.append('company_name', formData.companyName);
      const fullAddress = [formData.addressLine1, formData.addressLine2, formData.city, formData.stateProvince, formData.postalCode, formData.country].filter(Boolean).join(', ');
      formDataToSend.append('company_address', fullAddress);
      formDataToSend.append('address_line1',   formData.addressLine1);
      formDataToSend.append('address_line2',   formData.addressLine2 || '');
      formDataToSend.append('city',            formData.city);
      formDataToSend.append('state_province',  formData.stateProvince || '');
      formDataToSend.append('postal_code',     formData.postalCode);
      formDataToSend.append('country',         formData.country);
      formDataToSend.append('tax_registration_number', formData.taxRegistrationNumber);
      formDataToSend.append('subscription_plan', selectedPlan.id);
      formDataToSend.append('full_name',   formData.fullName);
      formDataToSend.append('designation', formData.designation);
      formDataToSend.append('email',       formData.email);
      formDataToSend.append('phone', `${formData.phoneCountryCode}${formData.phoneNumber}`);
      formDataToSend.append('timezone',    formData.timezone);
      formDataToSend.append('password',    formData.password);
      formDataToSend.append('terms_agreed', formData.termsAgreed);
      if (formData.companyLogo) {
        formDataToSend.append('company_logo', formData.companyLogo);
      }

      const regResponse = await fetch(API_ENDPOINTS.AUTH_REGISTER, {
        method: 'POST',
        body: formDataToSend,
      });

      const regResult = await regResponse.json();
      if (!regResponse.ok) {
        throw new Error(regResult.detail || 'Registration failed. Please try again.');
      }

      const companyId = regResult.company.id;

      // Save username so landing page can pre-fill login after Stripe redirect
      if (regResult.user?.username) {
        localStorage.setItem('quali5d_pending_username', regResult.user.username);
      }

      // ── STEP 2: Create Stripe Checkout session ──────────────────────────
      setSubmitStep('Preparing secure payment…');

      const baseUrl = API_ENDPOINTS.AUTH_REGISTER.replace('/api/auth/register', '');
      const checkoutResponse = await fetch(`${baseUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: String(companyId),
          plan: selectedPlan.id,
          email: formData.email,
        }),
      });

      const checkoutResult = await checkoutResponse.json();
      if (!checkoutResponse.ok) {
        throw new Error(checkoutResult.detail || 'Could not initialise payment. Please contact support.');
      }

      // ── STEP 3: Redirect to Stripe Checkout ────────────────────────────
      setSubmitStep('Redirecting to secure payment…');
      window.location.href = checkoutResult.checkout_url;

    } catch (error) {
      console.error('Registration error:', error);
      setIsSubmitting(false);
      setSubmitStep('');
      setErrors(prev => ({ ...prev, submit: error.message }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Sign Up for Quali5d — Start Your AI Recruitment Trial</title>
        <meta name="description" content="Register your organization for Quali5d. AI-powered technical interviews that learn your hiring manager's judgment. Get started in minutes." />
        <link rel="canonical" href="https://www.quali5d.com/register" />
        <meta property="og:title" content="Sign Up for Quali5d — Start Your AI Recruitment Trial" />
        <meta property="og:description" content="Register your organization for Quali5d. AI-powered technical interviews that learn your hiring manager's judgment. Get started in minutes." />
        <meta property="og:url" content="https://www.quali5d.com/register" />
        <meta name="twitter:title" content="Sign Up for Quali5d — Start Your AI Recruitment Trial" />
        <meta name="twitter:description" content="Register your organization for Quali5d. AI-powered technical interviews that learn your hiring manager's judgment. Get started in minutes." />
      </Helmet>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img 
              src="/Reqt5d-Logo.png" 
              alt="Reqt5d" 
              className="h-16 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <button 
              onClick={() => navigate('/pricing')}
              className="text-gray-700 hover:text-brand-blue font-medium"
            >
              ← Back to Pricing
            </button>
          </div>
        </div>
      </header>

      {/* Registration Form */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Complete Your Registration
            </h1>
            <p className="text-lg text-gray-600">
              You've selected: <span className="font-bold text-brand-blue">{selectedPlan.name} Plan (${selectedPlan.price}/month)</span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            
            {/* Company Details Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Company Information
              </h2>

              <div className="space-y-5">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.companyName ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                    }`}
                    placeholder="Enter your company name"
                  />
                  {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
                </div>

                {/* Company Address — Google Places Autocomplete */}
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-green-600">📍</span> Company Address
                  </h3>

                  {/* Address Line 1 — autocomplete */}
                  <div className="relative mb-3" ref={addressRef}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Address Line 1 * <span className="text-xs text-gray-400 font-normal">(start typing to search)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.addressLine1}
                        onChange={handleAddressSearch}
                        onFocus={() => formData.addressLine1.length > 2 && setShowSuggestions(true)}
                        className={`w-full px-4 py-3 pr-10 border-2 rounded-lg focus:outline-none transition-colors ${
                          errors.addressLine1 ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                        }`}
                        placeholder="Start typing your street address…"
                        autoComplete="off"
                      />
                      {addressLoading && (
                        <div className="absolute right-3 top-3.5">
                          <svg className="animate-spin w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    {/* Suggestions dropdown */}
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                        {addressSuggestions.map((s) => (
                          <button
                            key={s.place_id}
                            type="button"
                            onClick={() => handleSelectSuggestion(s.place_id, s.description, s._placePrediction)}
                            className="w-full text-left px-4 py-3 hover:bg-green-50 border-b border-gray-100 last:border-0 transition-colors"
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-green-600 mt-0.5 flex-shrink-0">📍</span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{s.structured_formatting?.main_text || s.description}</p>
                                <p className="text-xs text-gray-500">{s.structured_formatting?.secondary_text || ''}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 flex items-center gap-1 rounded-b-xl">
                          <img src="https://developers.google.com/static/maps/documentation/images/google_on_white.png" alt="Google" className="h-3"/>
                        </div>
                      </div>
                    )}
                    {errors.addressLine1 && <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>}
                  </div>

                  {/* Address Line 2 */}
                  <div className="mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Address Line 2 <span className="text-xs text-gray-400 font-normal">(Suite, Floor, Building — optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.addressLine2}
                      onChange={(e) => setFormData(prev => ({ ...prev, addressLine2: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-brand-green transition-colors"
                      placeholder="Suite 100, Floor 3, Building B…"
                    />
                  </div>

                  {/* City + State */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => { setFormData(prev => ({ ...prev, city: e.target.value })); if (errors.city) setErrors(prev => ({ ...prev, city: '' })); }}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.city ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'}`}
                        placeholder="City"
                      />
                      {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">State / Province</label>
                      <input
                        type="text"
                        value={formData.stateProvince}
                        onChange={(e) => setFormData(prev => ({ ...prev, stateProvince: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-brand-green transition-colors"
                        placeholder="State / Province / Region"
                      />
                    </div>
                  </div>

                  {/* Postal Code + Country */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Postal / ZIP / PIN Code *</label>
                      <input
                        type="text"
                        value={formData.postalCode}
                        onChange={(e) => { setFormData(prev => ({ ...prev, postalCode: e.target.value })); if (errors.postalCode) setErrors(prev => ({ ...prev, postalCode: '' })); }}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.postalCode ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'}`}
                        placeholder="e.g. 10001, SW1A 2AA, 400001"
                      />
                      {errors.postalCode && <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country *</label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => { setFormData(prev => ({ ...prev, country: e.target.value })); if (errors.country) setErrors(prev => ({ ...prev, country: '' })); }}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.country ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'}`}
                        placeholder="e.g. United States, India, UK"
                      />
                      {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>
                  </div>
                </div>

                {/* Tax Registration Number */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Tax Registration Number (EIN/VAT) *
                  </label>
                  <input
                    type="text"
                    name="taxRegistrationNumber"
                    value={formData.taxRegistrationNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.taxRegistrationNumber ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                    }`}
                    placeholder="e.g., 12-3456789"
                  />
                  {errors.taxRegistrationNumber && <p className="text-red-500 text-sm mt-1">{errors.taxRegistrationNumber}</p>}
                </div>

                {/* Company Logo */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Logo (Optional)
                  </label>
                  <div className="flex items-center gap-4">
                    {logoPreview && (
                      <div className="w-24 h-24 border-2 border-gray-300 rounded-lg overflow-hidden">
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-blue file:text-white hover:file:bg-brand-dark"
                      />
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                      {errors.companyLogo && <p className="text-red-500 text-sm mt-1">{errors.companyLogo}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Super User Details Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Super User Account
              </h2>

              <div className="space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.fullName ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                {/* Designation */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.designation ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                    }`}
                    placeholder="e.g., HR Director, Talent Acquisition Manager"
                  />
                  {errors.designation && <p className="text-red-500 text-sm mt-1">{errors.designation}</p>}
                </div>

                {/* Email & Phone */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Official Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                      }`}
                      placeholder="name@company.com"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <div className={`flex items-center border-2 rounded-xl transition-colors bg-white ${errors.phoneNumber ? 'border-red-400' : 'border-gray-200 focus-within:border-brand-green'}`}>
                      {/* Country code pill */}
                      <div className="relative flex-shrink-0" ref={countryDropdownRef}>
                        <button
                          type="button"
                          onClick={() => { setShowCountryDropdown(prev => !prev); setCountrySearch(''); }}
                          className="flex items-center gap-1.5 pl-3 pr-2 py-2.5 hover:bg-gray-50 rounded-l-xl transition-colors border-r border-gray-200 group min-w-[80px]"
                        >
                          <span className={`fi fi-${formData.phoneCountryIso} rounded-sm`} style={{fontSize:'16px', lineHeight:'1'}}></span>
                          <span className="text-xs font-semibold text-gray-700 tabular-nums">{formData.phoneCountryCode}</span>
                          <svg className="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                          </svg>
                        </button>

                        {showCountryDropdown && (
                          <div className="absolute z-50 top-full left-0 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden">
                            {/* Search */}
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <input
                                  type="text"
                                  value={countrySearch}
                                  onChange={(e) => setCountrySearch(e.target.value)}
                                  placeholder="Search…"
                                  className="bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none w-full"
                                  autoFocus
                                />
                              </div>
                            </div>
                            {/* List */}
                            <div className="overflow-y-auto max-h-56">
                              {filteredCountries.map((c, i) => {
                                const isSelected = formData.phoneCountryCode === c.code && formData.phoneCountryIso === c.iso;
                                return (
                                  <button
                                    key={`${c.iso}-${i}`}
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, phoneCountryCode: c.code, phoneCountryIso: c.iso }));
                                      setShowCountryDropdown(false);
                                      setCountrySearch('');
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors text-sm ${isSelected ? 'bg-green-50 text-green-800' : 'hover:bg-gray-50 text-gray-700'}`}
                                  >
                                    <span className={`fi fi-${c.iso} rounded-sm flex-shrink-0`} style={{fontSize:'18px',lineHeight:'1'}}></span>
                                    <span className="flex-1 truncate">{c.country}</span>
                                    <span className="text-xs font-semibold text-gray-400 tabular-nums">{c.code}</span>
                                    {isSelected && <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                                  </button>
                                );
                              })}
                              {filteredCountries.length === 0 && (
                                <p className="text-xs text-gray-400 text-center py-5">No countries found</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Number input */}
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, phoneNumber: e.target.value.replace(/[^0-9\s\-().]/g, '') }));
                          if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: '' }));
                        }}
                        className="flex-1 px-3 py-3 text-sm focus:outline-none bg-transparent rounded-r-xl placeholder-gray-400"
                        placeholder="Phone number"
                      />
                    </div>
                    {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
                  </div>
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Timezone *
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-brand-green transition-colors"
                  >
                    <option value="">Select Timezone</option>
                    <optgroup label="🇺🇸 United States">
                      <option value="America/New_York">Eastern Time (EST/EDT)</option>
                      <option value="America/Chicago">Central Time (CST/CDT)</option>
                      <option value="America/Denver">Mountain Time (MST/MDT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                      <option value="America/Anchorage">Alaska (AKST/AKDT)</option>
                      <option value="Pacific/Honolulu">Hawaii (HST)</option>
                    </optgroup>
                    <optgroup label="🇮🇳 India">
                      <option value="Asia/Kolkata">India Standard Time (IST)</option>
                    </optgroup>
                    <optgroup label="🇬🇧 United Kingdom">
                      <option value="Europe/London">UK Time (GMT/BST)</option>
                    </optgroup>
                    <optgroup label="🇪🇺 Europe">
                      <option value="Europe/Berlin">Central European (CET/CEST)</option>
                      <option value="Europe/Paris">France (CET/CEST)</option>
                      <option value="Europe/Amsterdam">Netherlands (CET/CEST)</option>
                      <option value="Europe/Bucharest">Eastern European (EET/EEST)</option>
                    </optgroup>
                    <optgroup label="🇨🇦 Canada">
                      <option value="America/Toronto">Eastern (EST/EDT)</option>
                      <option value="America/Vancouver">Pacific (PST/PDT)</option>
                      <option value="America/Edmonton">Mountain (MST/MDT)</option>
                    </optgroup>
                    <optgroup label="🌏 Asia Pacific">
                      <option value="Asia/Dubai">UAE / Gulf (GST)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Asia/Tokyo">Japan (JST)</option>
                      <option value="Asia/Shanghai">China (CST)</option>
                      <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                      <option value="Asia/Seoul">South Korea (KST)</option>
                      <option value="Asia/Manila">Philippines (PHT)</option>
                      <option value="Asia/Kuala_Lumpur">Malaysia (MYT)</option>
                      <option value="Asia/Jakarta">Indonesia Western (WIB)</option>
                    </optgroup>
                    <optgroup label="🇦🇺 Australia / NZ">
                      <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                      <option value="Australia/Melbourne">Melbourne (AEST/AEDT)</option>
                      <option value="Australia/Perth">Perth (AWST)</option>
                      <option value="Pacific/Auckland">New Zealand (NZST/NZDT)</option>
                    </optgroup>
                    <optgroup label="🌎 Latin America">
                      <option value="America/Mexico_City">Mexico City (CST/CDT)</option>
                      <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                      <option value="America/Argentina/Buenos_Aires">Buenos Aires (ART)</option>
                      <option value="America/Bogota">Colombia (COT)</option>
                    </optgroup>
                    <optgroup label="🌍 Africa / Middle East">
                      <option value="Africa/Johannesburg">South Africa (SAST)</option>
                      <option value="Africa/Lagos">Nigeria / West Africa (WAT)</option>
                      <option value="Africa/Nairobi">East Africa (EAT)</option>
                      <option value="Asia/Riyadh">Saudi Arabia (AST)</option>
                      <option value="Asia/Jerusalem">Israel (IST/IDT)</option>
                    </optgroup>
                  </select>
                  {formData.timezone && (
                    <p className="text-sm text-blue-600 mt-1">
                      🕐 Current time: {new Date().toLocaleTimeString('en-US', { timeZone: formData.timezone, hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' })}
                    </p>
                  )}
                </div>

                {/* Password & Confirm Password */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.password ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                      }`}
                      placeholder="Min. 8 characters"
                    />
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300 focus:border-brand-green'
                      }`}
                      placeholder="Re-enter password"
                    />
                    {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Summary */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-3 border-b-2 border-gray-200">
                Plan &amp; Payment
              </h2>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Selected Plan</div>
                  <div className="text-xl font-black text-brand-blue">{selectedPlan.name}</div>
                  <div className="text-sm text-gray-600 mt-0.5">Billed monthly · Cancel anytime</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black text-gray-900">${selectedPlan.price}</div>
                  <div className="text-sm text-gray-500">/month</div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                </svg>
                You will be redirected to Stripe's secure checkout to complete payment after registration.
              </p>
            </div>

            {/* Terms Agreement */}
            <div className="mb-8">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="termsAgreed"
                  checked={formData.termsAgreed}
                  onChange={handleChange}
                  className="w-5 h-5 mt-1 accent-brand-green cursor-pointer"
                  id="termsCheckbox"
                />
                <label htmlFor="termsCheckbox" className="text-sm text-gray-700 cursor-pointer select-none">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setDrawerContent('terms'); setDrawerOpen(true); }}
                    className="text-brand-blue hover:underline font-semibold"
                  >
                    Quali5d Subscription &amp; AI Recruitment Services Agreement
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setDrawerContent('privacy'); setDrawerOpen(true); }}
                    className="text-brand-blue hover:underline font-semibold"
                  >
                    Privacy Policy
                  </button>{' '}*
                </label>
              </div>
              {errors.termsAgreed && <p className="text-red-500 text-sm mt-2 ml-8">{errors.termsAgreed}</p>}
            </div>

            {/* Submit error */}
            {errors.submit && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-red-700 text-sm font-medium">❌ {errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-lg font-bold text-lg shadow-lg transition-all ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-brand-green to-brand-teal text-white hover:shadow-xl hover:-translate-y-0.5'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  {submitStep || 'Processing…'}
                </span>
              ) : (
                'Complete Registration & Pay →'
              )}
            </button>
            <p className="text-center text-xs text-gray-400 mt-3">
              🔒 Secured by Stripe · Your payment details are never stored on our servers
            </p>
          </form>
        </div>
      </section>

      {/* ── Legal Document Drawer ─────────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer */}
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {drawerContent === 'terms'
                    ? 'Terms of Service & Subscription Agreement'
                    : 'Privacy Policy'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Uni5d Technology Solutions LLC · Effective May 4, 2026</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 flex-shrink-0">
              <button
                onClick={() => setDrawerContent('terms')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  drawerContent === 'terms'
                    ? 'border-brand-blue text-brand-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Terms of Service
              </button>
              <button
                onClick={() => setDrawerContent('privacy')}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  drawerContent === 'privacy'
                    ? 'border-brand-blue text-brand-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Privacy Policy
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-8 py-6 text-sm text-gray-700 leading-relaxed">
              {drawerContent === 'terms' ? (
                <div>
                  <p className="text-xs text-gray-400 mb-6">For the complete Terms of Service, visit <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue underline">quali5d.com/terms</a></p>

                  <h3 className="font-bold text-gray-900 mb-2">1. Acceptance</h3>
                  <p className="mb-4">By registering, you agree to be bound by these Terms of Service between you and Uni5d Technology Solutions LLC (EIN: 42-1896513), a North Carolina LLC operating the Quali5d platform.</p>

                  <h3 className="font-bold text-gray-900 mb-2">2. Subscriptions & Billing</h3>
                  <p className="mb-4">Subscriptions are billed monthly. No prorated refunds are issued for unused periods. Add-on packs are valid for 30 days and non-refundable. Prices exclude applicable taxes (NC: 7.25%).</p>

                  <h3 className="font-bold text-gray-900 mb-2">3. Your Data</h3>
                  <p className="mb-4">You retain ownership of all data you upload. You grant Uni5d a limited license to process it for service delivery. Audio/video recordings are retained for 90 days then permanently deleted.</p>

                  <h3 className="font-bold text-gray-900 mb-2">4. AI Services</h3>
                  <p className="mb-4">All AI-generated outputs are advisory only and must not be used as the sole basis for employment decisions. You are responsible for compliance with applicable employment and AI regulation laws in your jurisdiction.</p>

                  <h3 className="font-bold text-gray-900 mb-2">5. Candidate Consent</h3>
                  <p className="mb-4">You must obtain explicit informed consent from candidates before scheduling them for AI interviews. Candidates must be told the interview is AI-conducted and will be recorded.</p>

                  <h3 className="font-bold text-gray-900 mb-2">6. Liability</h3>
                  <p className="mb-4">Liability is capped at fees paid in the prior 12 months. We are not liable for indirect or consequential damages. The platform is provided "as is" without warranties.</p>

                  <h3 className="font-bold text-gray-900 mb-2">7. Governing Law</h3>
                  <p className="mb-4">These Terms are governed by the laws of North Carolina, USA. Disputes are resolved by binding arbitration in Iredell County, NC.</p>

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-xs text-blue-700">
                      <strong>Full document:</strong> The complete Terms of Service with all clauses is available at{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-semibold">quali5d.com/terms</a>
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-gray-400 mb-6">For the complete Privacy Policy, visit <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue underline">quali5d.com/privacy</a></p>

                  <h3 className="font-bold text-gray-900 mb-2">1. What We Collect</h3>
                  <p className="mb-4">We collect company and user information provided during registration, job descriptions and recordings you upload, AI-generated documents, and usage/log data. Payment details are handled exclusively by Stripe — we never store card numbers.</p>

                  <h3 className="font-bold text-gray-900 mb-2">2. How We Use It</h3>
                  <p className="mb-4">Data is used solely to provide the platform services: processing requirements, conducting AI calls, generating assessments, and managing your account. We do not sell your data or use it for advertising.</p>

                  <h3 className="font-bold text-gray-900 mb-2">3. AI Processing</h3>
                  <p className="mb-4">Content is processed by Anthropic Claude (document generation), ElevenLabs (AI voice conversations), and Microsoft Azure Speech Services (transcription). All are contractually bound data processors.</p>

                  <h3 className="font-bold text-gray-900 mb-2">4. Data Retention</h3>
                  <p className="mb-4">Audio and video recordings: <strong>90 days</strong> then permanently deleted. Transcripts and documents: retained for your subscription period plus 30 days. Billing records: 7 years (legal requirement).</p>

                  <h3 className="font-bold text-gray-900 mb-2">5. Your Rights</h3>
                  <p className="mb-4">You have rights to access, correct, delete, and export your data. EU/EEA users have GDPR rights including the right to object to automated decisions. California users have CCPA rights. Contact admin@uni5d.ai.</p>

                  <h3 className="font-bold text-gray-900 mb-2">6. Security</h3>
                  <p className="mb-4">All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Hosted on Microsoft Azure with SOC 2 Type II certified infrastructure.</p>

                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-xs text-green-700">
                      <strong>Full document:</strong> The complete Privacy Policy with all details is available at{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline font-semibold">quali5d.com/privacy</a>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                By checking the box on the registration form you agree to both documents.
              </p>
              <button
                onClick={() => setDrawerOpen(false)}
                className="px-5 py-2 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default RegistrationPage;